import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft,
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  UserCheck, 
  UserX,
  AlertCircle,
  RefreshCw,
  Search,
  Shield,
  User,
  Settings,
  Building2
} from 'lucide-react'
import { userService, UserData, CreateUserRequest, UserSector } from '../services/userService'
import UserBotPermissions from '../components/UserBotPermissions'
import toast from 'react-hot-toast'
import { logger } from '../utils/logger'

const UserManagement: React.FC = () => {
  const navigate = useNavigate()
  const [users, setUsers] = useState<UserData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showPermissionsModal, setShowPermissionsModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showSectorModal, setShowSectorModal] = useState(false)
  const [userToDelete, setUserToDelete] = useState<UserData | null>(null)
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null)
  const [availableSectors, setAvailableSectors] = useState<UserSector[]>([])
  const [formData, setFormData] = useState<CreateUserRequest>({
    name: '',
    email: '',
    password: '',
    type: 'attendant',
    status: 'active'
  })

  const loadUsers = async (page = 1) => {
    try {
      logger.debug('🔄 Carregando usuários, página:', page)
      setLoading(true)
      const response = await userService.getUsers(page, pagination.limit)
      logger.debug('📋 Usuários carregados', { total: response.users?.length || 0 })
      setUsers(response.users)
      setPagination(response.pagination)
    } catch (err: any) {
      logger.error('❌ Erro ao carregar usuários:', err)
      setError(err.response?.data?.message || 'Erro ao carregar usuários')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUsers()
    loadSectors()
  }, [])

  const loadSectors = async () => {
    try {
      const sectors = await userService.getSectors()
      setAvailableSectors(sectors)
    } catch (err: any) {
      logger.error('Erro ao carregar setores:', err)
    }
  }

  const handleCreateUser = async (e: React.FormEvent, _userId?: number, modalFormData?: CreateUserRequest) => {
    e.preventDefault()
    try {
      // Usar formData do modal se fornecido, caso contrário usar do estado do componente
      const dataToCreate = modalFormData || formData
      logger.debug('🔄 Criando usuário', { type: dataToCreate.type, status: dataToCreate.status, hasPassword: Boolean(dataToCreate.password) })
      await userService.createUser(dataToCreate)
      toast.success('Usuário criado com sucesso!')
      setShowCreateModal(false)
      setFormData({
        name: '',
        email: '',
        password: '',
        type: 'attendant',
        status: 'active'
      })
      loadUsers(pagination.page)
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao criar usuário'
      toast.error(errorMessage)
      setError(errorMessage)
    }
  }


  const handleDeleteClick = (user: UserData) => {
    setUserToDelete(user)
    setShowDeleteModal(true)
  }

  const handleConfirmDelete = async () => {
    if (!userToDelete) return

    try {
      await userService.deleteUser(userToDelete.id)
      toast.success('Usuário deletado com sucesso!')
      setShowDeleteModal(false)
      setUserToDelete(null)
      loadUsers(pagination.page)
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao deletar usuário'
      toast.error(errorMessage)
      setError(errorMessage)
    }
  }

  const handleOpenPermissions = (user: UserData) => {
    setSelectedUser(user)
    setShowPermissionsModal(true)
  }

  const handleClosePermissions = () => {
    setShowPermissionsModal(false)
    setSelectedUser(null)
  }

  const handleSavePermissions = () => {
    setShowPermissionsModal(false)
    setSelectedUser(null)
    loadUsers(pagination.page) // Recarregar lista se necessário
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'blocked':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'manager':
        return 'bg-purple-100 text-purple-800'
      case 'attendant':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-200 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Gerenciamento de Atendentes
                </h1>
                <p className="text-gray-600">Gerencie os usuários que farão atendimento humano</p>
              </div>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              <span>Novo Atendente</span>
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            {error}
          </div>
        )}

        {/* Search and Actions */}
        <div className="mb-6 bg-white rounded-lg shadow-sm p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Buscar por nome ou e-mail..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <button
              onClick={() => loadUsers(pagination.page)}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto text-blue-500" />
              <p className="mt-2 text-gray-600">Carregando usuários...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="h-12 w-12 mx-auto text-gray-400" />
              <h3 className="mt-2 text-lg font-medium text-gray-900">Nenhum usuário encontrado</h3>
              <p className="mt-1 text-gray-500">
                {searchTerm ? 'Tente ajustar os termos de busca.' : 'Comece criando um novo atendente.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usuário
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Setores
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Criado em
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <User className="h-5 w-5 text-blue-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(user.type)}`}>
                          {user.type === 'manager' ? (
                            <>
                              <Shield className="h-3 w-3 mr-1" />
                              Gerente
                            </>
                          ) : (
                            <>
                              <UserCheck className="h-3 w-3 mr-1" />
                              Atendente
                            </>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.status)}`}>
                          {user.status === 'active' ? (
                            <>
                              <UserCheck className="h-3 w-3 mr-1" />
                              Ativo
                            </>
                          ) : (
                            <>
                              <UserX className="h-3 w-3 mr-1" />
                              Bloqueado
                            </>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {user.sectors && user.sectors.length > 0
                          ? user.sectors.map((sector) => sector.name).join(', ')
                          : 'Sem setor'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.created_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleOpenPermissions(user)}
                            className="text-purple-600 hover:text-purple-900"
                            title="Gerenciar permissões de bots"
                          >
                            <Settings className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(user)
                              setShowSectorModal(true)
                            }}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Associar setores"
                          >
                            <Building2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(user)
                              setFormData({
                                name: user.name,
                                email: user.email,
                                password: '',
                                type: user.type,
                                status: user.status
                              })
                              setShowEditModal(true)
                            }}
                            className="text-blue-600 hover:text-blue-900"
                            title="Editar usuário"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(user)}
                            className="text-red-600 hover:text-red-900"
                            title="Deletar usuário"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => loadUsers(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                onClick={() => loadUsers(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próximo
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Mostrando <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> até{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>{' '}
                  de <span className="font-medium">{pagination.total}</span> resultados
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => loadUsers(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => loadUsers(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Próximo
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <UserModal
          key={`create-${showCreateModal}`}
          user={null}
          onClose={() => {
            setShowCreateModal(false)
            setFormData({
              name: '',
              email: '',
              password: '',
              type: 'attendant',
              status: 'active'
            })
          }}
          onSubmit={async (e, userId, modalFormData) => {
            e.preventDefault()
            logger.debug('🔄 handleCreateUser chamado', { userId, hasModalData: Boolean(modalFormData) })
            await handleCreateUser(e, userId, modalFormData)
          }}
        />
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <UserModal
          key={`edit-${selectedUser.id}-${showEditModal}`}
          user={selectedUser}
          onClose={() => {
            setShowEditModal(false)
            setSelectedUser(null)
            setFormData({
              name: '',
              email: '',
              password: '',
              type: 'attendant',
              status: 'active'
            })
          }}
          onSubmit={(e, userId, modalFormData) => {
            e.preventDefault()
            if (!userId || !modalFormData) return
            // Criar uma função que passa o formData do modal
            const handleSubmit = async () => {
              try {
                logger.debug('🔄 Atualizando usuário do modal', { userId, hasPassword: Boolean(modalFormData.password) })
                await userService.updateUser(userId, modalFormData)
                logger.debug('✅ Usuário atualizado com sucesso do modal')
                toast.success('Usuário atualizado com sucesso!')
                setShowEditModal(false)
                setSelectedUser(null)
                loadUsers(pagination.page)
              } catch (err: any) {
                logger.error('❌ Erro ao atualizar usuário do modal:', err)
                const errorMessage = err.response?.data?.message || 'Erro ao atualizar usuário'
                toast.error(errorMessage)
                setError(errorMessage)
              }
            }
            handleSubmit()
          }}
        />
      )}

      {/* Modal de Permissões de Bots */}
      {showPermissionsModal && selectedUser && (
        <UserBotPermissions
          userId={selectedUser.id}
          userName={selectedUser.name}
          onClose={handleClosePermissions}
          onSave={handleSavePermissions}
        />
      )}

      {showSectorModal && selectedUser && (
        <UserSectorModal
          user={selectedUser}
          availableSectors={availableSectors}
          onClose={() => {
            setShowSectorModal(false)
            setSelectedUser(null)
          }}
          onCreateSector={async (name) => {
            const created = await userService.createSector(name)
            setAvailableSectors((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)))
          }}
          onUpdateSector={async (sectorId, name) => {
            const updated = await userService.updateSector(sectorId, name)
            setAvailableSectors((prev) =>
              prev.map((sector) => (sector.id === sectorId ? updated : sector)).sort((a, b) => a.name.localeCompare(b.name))
            )
          }}
          onDeleteSector={async (sectorId) => {
            await userService.deleteSector(sectorId)
            setAvailableSectors((prev) => prev.filter((sector) => sector.id !== sectorId))
          }}
          onSave={async (sectorIds) => {
            await userService.updateUser(selectedUser.id, { sector_ids: sectorIds })
            toast.success('Setores atualizados com sucesso!')
            setShowSectorModal(false)
            setSelectedUser(null)
            await loadUsers(pagination.page)
          }}
        />
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && userToDelete && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDeleteModal(false)
              setUserToDelete(null)
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </div>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Confirmar Exclusão
                  </h3>
                </div>
              </div>
              <div className="mb-6">
                <p className="text-sm text-gray-600">
                  Tem certeza que deseja excluir o usuário <strong>"{userToDelete.name}"</strong> ({userToDelete.email})?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Esta ação não pode ser desfeita e todas as permissões associadas serão removidas.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false)
                    setUserToDelete(null)
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Componente Modal de Usuário
interface UserModalProps {
  user: UserData | null
  onClose: () => void
  onSubmit: (e: React.FormEvent, userId?: number, modalFormData?: CreateUserRequest) => void
}

const UserModal: React.FC<UserModalProps> = ({
  user,
  onClose,
  onSubmit
}) => {
  const [formData, setFormData] = useState<CreateUserRequest>({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    type: user?.type || 'attendant',
    status: user?.status || 'active'
  })

  // Log quando formData mudar
  React.useEffect(() => {
    logger.debug('📝 Modal: formData atualizado', { type: formData.type, status: formData.status, hasPassword: Boolean(formData.password), hasName: Boolean(formData.name), hasEmail: Boolean(formData.email) })
  }, [formData])

  // Atualizar formData quando user mudar (apenas na montagem ou quando user realmente mudar)
  React.useEffect(() => {
    if (user) {
      logger.debug('🔄 Modal: Carregando dados do usuário', { userId: user.id, type: user.type, status: user.status })
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        type: user.type || 'attendant',
        status: user.status || 'active'
      })
    } else {
      logger.debug('🔄 Modal: Criando novo usuário - resetando formData')
      setFormData({
        name: '',
        email: '',
        password: '',
        type: 'attendant',
        status: 'active'
      })
    }
  }, [user?.id]) // Usar user?.id para evitar re-execuções desnecessárias

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-16 mx-auto w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-gray-100">
        <div className="mt-3">
          <div className="px-6 pt-6 pb-2 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">
              {user ? 'Editar Usuário' : 'Novo Atendente'}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {user ? 'Atualize os dados do atendente.' : 'Cadastre um novo atendente para atendimento humano.'}
            </p>
          </div>
          <form onSubmit={(e) => {
            e.preventDefault()
            logger.debug('📤 Form submit', { isEdit: Boolean(user?.id), type: formData.type, status: formData.status, hasPassword: Boolean(formData.password) })
            onSubmit(e, user?.id, formData)
          }} className="space-y-4 px-6 py-5">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nome</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => {
                  logger.debug('📝 Input name atualizado', { hadValue: Boolean(formData.name), hasValue: Boolean(e.target.value) })
                  setFormData({ ...formData, name: e.target.value })
                }}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Senha {user && '(deixe em branco para manter a atual)'}
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required={!user}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Tipo</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as 'attendant' | 'manager' })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="attendant">Atendente</option>
                <option value="manager">Gerente</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'blocked' })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="active">Ativo</option>
                <option value="blocked">Bloqueado</option>
              </select>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-md btn-outline"
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-md btn-primary">
                {user ? 'Atualizar' : 'Criar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

interface UserSectorModalProps {
  user: UserData
  availableSectors: UserSector[]
  onClose: () => void
  onSave: (sectorIds: number[]) => Promise<void>
  onCreateSector: (name: string) => Promise<void>
  onUpdateSector: (sectorId: number, name: string) => Promise<void>
  onDeleteSector: (sectorId: number) => Promise<void>
}

const UserSectorModal: React.FC<UserSectorModalProps> = ({
  user,
  availableSectors,
  onClose,
  onSave,
  onCreateSector,
  onUpdateSector,
  onDeleteSector
}) => {
  const [selectedSectorIds, setSelectedSectorIds] = useState<number[]>(user.sectors?.map((s) => s.id) || [])
  const [newSectorName, setNewSectorName] = useState('')
  const [editingSectorId, setEditingSectorId] = useState<number | null>(null)
  const [editingSectorName, setEditingSectorName] = useState('')
  const [activeTab, setActiveTab] = useState<'associate' | 'manage'>('associate')
  const [saving, setSaving] = useState(false)

  return (
    <div className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-12 mx-auto w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-gray-100">
        <div className="px-6 py-5 border-b border-gray-100">
          <h3 className="text-xl font-semibold text-gray-900">Associar setores</h3>
          <p className="text-sm text-gray-500 mt-1">Gerencie os setores do usuário <span className="font-medium">{user.name}</span>.</p>
          <div className="mt-4 inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
            <button
              type="button"
              onClick={() => setActiveTab('associate')}
              className={`px-3 py-1.5 text-sm rounded-md ${activeTab === 'associate' ? 'bg-white text-gray-900 shadow' : 'text-gray-600'}`}
            >
              Associação
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('manage')}
              className={`px-3 py-1.5 text-sm rounded-md ${activeTab === 'manage' ? 'bg-white text-gray-900 shadow' : 'text-gray-600'}`}
            >
              Gerenciar setores
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {activeTab === 'associate' ? (
            <>
              <div className="flex flex-wrap gap-2">
                {(user.sectors || []).map((sector) => (
                  <span key={sector.id} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs">
                    {sector.name}
                    <button
                      type="button"
                      className="text-red-600 hover:text-red-800"
                      title="Remover associação"
                      onClick={() => {
                        setSelectedSectorIds((prev) => prev.filter((id) => id !== sector.id))
                      }}
                    >
                      Remover
                    </button>
                  </span>
                ))}
                {(!user.sectors || user.sectors.length === 0) && (
                  <span className="text-xs text-gray-500">Usuário sem setor associado.</span>
                )}
              </div>
              <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-xl p-3">
                {availableSectors.length === 0 ? (
                  <p className="text-sm text-gray-500">Nenhum setor cadastrado. Vá para a aba "Gerenciar setores".</p>
                ) : (
                  availableSectors.map((sector) => {
                    const checked = selectedSectorIds.includes(sector.id)
                    return (
                      <label key={sector.id} className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-gray-50">
                        <span className="text-sm text-gray-700">{sector.name}</span>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setSelectedSectorIds((prev) =>
                              e.target.checked ? Array.from(new Set([...prev, sector.id])) : prev.filter((id) => id !== sector.id)
                            )
                          }}
                        />
                      </label>
                    )
                  })
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newSectorName}
                  onChange={(e) => setNewSectorName(e.target.value)}
                  placeholder="Nome do novo setor"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={async () => {
                    const name = newSectorName.trim()
                    if (!name) return
                    await onCreateSector(name)
                    setNewSectorName('')
                  }}
                >
                  Criar setor
                </button>
              </div>
              <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-xl p-3">
                {availableSectors.length === 0 ? (
                  <p className="text-sm text-gray-500">Nenhum setor cadastrado.</p>
                ) : (
                  availableSectors.map((sector) => (
                    <div key={sector.id} className="flex items-center gap-2 py-2">
                      {editingSectorId === sector.id ? (
                        <>
                          <input
                            type="text"
                            value={editingSectorName}
                            onChange={(e) => setEditingSectorName(e.target.value)}
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={async () => {
                              const nextName = editingSectorName.trim()
                              if (!nextName) return
                              await onUpdateSector(sector.id, nextName)
                              setEditingSectorId(null)
                              setEditingSectorName('')
                            }}
                          >
                            Salvar
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            onClick={() => {
                              setEditingSectorId(null)
                              setEditingSectorName('')
                            }}
                          >
                            Cancelar
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="flex-1 text-sm text-gray-700">{sector.name}</div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            onClick={() => {
                              setEditingSectorId(sector.id)
                              setEditingSectorName(sector.name)
                            }}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline text-red-600 border-red-200 hover:bg-red-50"
                            onClick={async () => {
                              await onDeleteSector(sector.id)
                              setSelectedSectorIds((prev) => prev.filter((id) => id !== sector.id))
                            }}
                          >
                            Excluir
                          </button>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end space-x-3">
          <button type="button" onClick={onClose} className="btn btn-md btn-outline">
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-md btn-primary"
            disabled={saving}
            onClick={async () => {
              setSaving(true)
              try {
                await onSave(selectedSectorIds)
              } finally {
                setSaving(false)
              }
            }}
          >
            Salvar associação
          </button>
        </div>
      </div>
    </div>
  )
}

export default UserManagement
